import * as Api from '@ViewModels';
import { THEME } from '@Theme';
import { RichTextEditor } from '@levitate/rich-text';
import { StyleSheet, css } from 'aphrodite';
import moment from 'moment';
import { useState } from 'react';
import { InternalMeetingNoteType } from '../../../../models/AdminModels';
import { invalidateAccountAdminQuery } from '../../../../queries/Admin/Account/useAdminAccountQuery';
import { useUpdateNextMeetingMutation } from '../../../../queries/Admin/Account/useUpdateNextMeetingMutation';
import { invalidateNoteById } from '../../../../queries/Note/useNoteByIdQuery';
import { useUpdateAccountNoteMutation } from '../../../../queries/Note/useUpdateAccountNoteMutation';
import { Button } from '../../../../web/components-v2/Button';
import { DateInput } from '../../../../web/components-v2/DateInput';
import { Dialog, DialogPopup, DialogTitle } from '../../../../web/components-v2/Dialog';
import { actionItemDraftsToApi, type IActionItemDraft, MeetingActionItems } from './MeetingActionItems';
import { buildCcReferencedEntities, NoteCcField } from './NoteCcField';

/** Footer "Next Meeting" glyph — a calendar tinted with the warning color, matching the meeting composer. */
const CalendarGlyph = () => (
	<svg
		width={18}
		height={18}
		viewBox='0 0 24 24'
		fill='none'
		stroke={THEME.colors.warning.dark}
		strokeWidth={2}
		strokeLinecap='round'
		strokeLinejoin='round'
	>
		<rect x='3' y='4' width='18' height='18' rx='2' />
		<line x1='16' y1='2' x2='16' y2='6' />
		<line x1='8' y1='2' x2='8' y2='6' />
		<line x1='3' y1='10' x2='21' y2='10' />
	</svg>
);

export interface IAccountNoteComposerProps {
	/** The account the note hangs off of. */
	accountId: string;
	/** Which internal note slot is being created/edited. */
	type: InternalMeetingNoteType;
	/** Section title (e.g. "Summary from CSM") — used in the dialog heading. */
	title: string;
	/**
	 * Whether this opens a blank composer for a brand-new note (`add`, the CSM "Add a Note" flow) or
	 * edits the note currently displayed (`edit`, the Sales / Summary pencil). In `add` mode the
	 * editor is always seeded empty and the existing `noteId` is never sent, so saving can only ever
	 * append a note — it can't overwrite the one on screen.
	 */
	mode: 'add' | 'edit';
	/** The existing note's HTML (via the content converter). Ignored in `add` mode. */
	initialHtml: string;
	/** The id of the currently-displayed note of this `type`, if any — invalidated on save. */
	noteId?: string;
	/** The account's current next CSM meeting; seeds + gates the Next Meeting field (CSM only). */
	nextMeeting?: Date | string;
	/** Whether the dialog is shown. */
	isOpen: boolean;
	/** Called when the dialog requests close (save or cancel). */
	onClose: () => void;
}

/**
 * Account Overview note editor — no MobX. Wraps the shared TipTap `RichTextEditor` (HTML in/out)
 * in a components-v2 `Dialog`, matching the "Add Meeting Notes" design: a white header, a padded
 * body (Add Cc + editor), and a footer with Save plus (for the CSM note) a "Next Meeting" link that
 * reveals the date picker — the same footer treatment as the meeting composer. On Save the latest
 * HTML is converted to a raw rich-text content state and persisted through
 * {@link useUpdateAccountNoteMutation}; the note + account queries are invalidated so the read-only
 * display refetches, then the dialog closes. The next meeting is persisted (when changed) via
 * {@link useUpdateNextMeetingMutation}.
 */
export const AccountNoteComposer = ({
	accountId,
	type,
	title,
	mode,
	initialHtml,
	noteId,
	nextMeeting,
	isOpen,
	onClose,
}: IAccountNoteComposerProps) => {
	const isAdd = mode === 'add';
	// Legacy parity: "Add a Note" opens a blank editor (`copyContentFromExistingNote = false`), while
	// the Sales / Summary pencil seeds from the note on screen. Deriving it here rather than at the
	// call site keeps the two modes from drifting apart.
	const seedHtml = isAdd ? '' : initialHtml;
	const [html, setHtml] = useState(seedHtml);
	const [ccUsers, setCcUsers] = useState<Api.IUser[]>([]);
	const [actionItems, setActionItems] = useState<IActionItemDraft[]>([]);
	const updateNote = useUpdateAccountNoteMutation();
	const updateNextMeeting = useUpdateNextMeetingMutation();

	// Legacy: the Next Meeting affordance is a CSM-note concept only.
	const showNextMeeting = type === InternalMeetingNoteType.CSM;
	// Seed in UTC to match the read display + legacy (nextMeeting is a UTC-midnight date); local
	// parsing would roll it back a day and re-save the shift.
	const currentNextMeeting = nextMeeting ? moment.utc(nextMeeting).format('YYYY-MM-DD') : '';
	const [nextMeetingValue, setNextMeetingValue] = useState(currentNextMeeting);
	const [showNextMeetingField, setShowNextMeetingField] = useState(false);

	const isSaving = updateNote.isPending || updateNextMeeting.isPending;

	const nextMeetingLabel = nextMeetingValue
		? `Next Meeting: ${moment(nextMeetingValue).format('MMM D, YYYY')}`
		: 'Next Meeting: Not Set!';

	const onSave = async () => {
		const content = Api.createRawRichTextContentStateWithHtmlStringValue(html);
		const saved = await updateNote.mutateAsync({
			accountId,
			type,
			// Never forward the displayed note's id when adding — that is what turns an "Add a Note"
			// into an in-place edit of the note already on screen.
			noteId: isAdd ? undefined : noteId,
			content,
			referencedEntities: buildCcReferencedEntities(ccUsers),
			actionItems: actionItemDraftsToApi(actionItems),
		});
		// Persist the next meeting only when it changed (a separate PUT, like the legacy composer).
		if (showNextMeeting && nextMeetingValue && nextMeetingValue !== currentNextMeeting) {
			await updateNextMeeting.mutateAsync({ accountId, nextMeeting: nextMeetingValue });
		}
		if (saved) {
			invalidateNoteById(noteId);
			invalidateAccountAdminQuery();
		}
		onClose();
	};

	return (
		<Dialog open={isOpen} onOpenChange={open => !open && onClose()} dismissOnOutsideClick={false}>
			<DialogPopup styles={[s.popup]}>
				<div className={css(s.header)}>
					<DialogTitle styles={[s.title]}>{isAdd ? 'Add a Note' : `Edit ${title}`}</DialogTitle>
				</div>
				<div className={css(s.body)}>
					<NoteCcField ccUsers={ccUsers} onChange={setCcUsers} />
					<RichTextEditor
						initialContent={seedHtml}
						placeholder='Add a note…'
						showLink
						onChange={nextHtml => setHtml(nextHtml)}
					/>
					<MeetingActionItems items={actionItems} onChange={setActionItems} />
				</div>
				<div className={css(s.footer)}>
					<div className={css(s.footerRow)}>
						<Button onClick={onSave} isLoading={isSaving}>
							Save
						</Button>
						{showNextMeeting && (
							<Button variant='link' styles={[s.footLink]} onClick={() => setShowNextMeetingField(open => !open)}>
								<CalendarGlyph />
								{nextMeetingLabel}
							</Button>
						)}
					</div>
					{showNextMeeting && showNextMeetingField && (
						<DateInput value={nextMeetingValue} onChange={value => setNextMeetingValue(value)} />
					)}
				</div>
			</DialogPopup>
		</Dialog>
	);
};

/**
 * Chrome for the single-column note composer, matching the two-pane meeting composer's design feel:
 * an edge-to-edge white header (sora/primary title + built-in close X), a padded scrolling body, and
 * a bordered footer with a left-aligned Save + Next Meeting link. All tokens come from `THEME`.
 */
const s = StyleSheet.create({
	popup: {
		width: 720,
		maxWidth: '100%',
		maxHeight: '90vh',
		padding: 0,
		overflow: 'hidden',
	},
	header: {
		flex: 'none',
		alignItems: 'center',
		display: 'flex',
		justifyContent: 'space-between',
		padding: `${THEME.spacing.lg - 4}px ${THEME.spacing.lg}px`,
		borderBottom: `1px solid ${THEME.colors.border.light}`,
	},
	title: {
		fontFamily: THEME.typography.fontFamilies.sora,
		fontSize: THEME.typography.fontSizes.xl,
		fontWeight: THEME.typography.fontWeights.bold,
		color: THEME.colors.primary.main,
		margin: 0,
	},
	body: {
		display: 'flex',
		flex: 1,
		flexDirection: 'column',
		gap: THEME.spacing.md,
		minHeight: 0,
		overflowY: 'auto',
		padding: `${THEME.spacing.lg - 2}px ${THEME.spacing.lg}px`,
	},
	footer: {
		display: 'flex',
		flex: 'none',
		flexDirection: 'column',
		gap: THEME.spacing.md,
		padding: `${THEME.spacing.md}px ${THEME.spacing.lg}px`,
		borderTop: `1px solid ${THEME.colors.border.light}`,
		backgroundColor: THEME.colors.background.main,
	},
	footerRow: {
		alignItems: 'center',
		display: 'flex',
		flexWrap: 'wrap',
		gap: THEME.spacing.lg,
	},
	// A footer action styled as a link (Next Meeting): neutral text, colored icon — mirrors the meeting composer.
	footLink: {
		alignItems: 'center',
		color: THEME.colors.text.main,
		display: 'inline-flex',
		fontWeight: THEME.typography.fontWeights.semibold,
		gap: THEME.spacing.sm,
		height: 'auto',
		padding: 0,
		width: 'fit-content',
		':hover': {
			color: THEME.colors.primary.main,
		},
	},
});
